import { ApolloError, useReactiveVar } from '@apollo/client';

import {
  ShortLinkClickListQuery,
  ShortLinkDailyClickListQuery,
  ShortLinkFacetListQuery,
  ShortLinkListQuery,
  useShortLinkCardQuery,
  useShortLinkClickListQuery,
  useShortLinkDailyClickListQuery,
  useShortLinkFacetListQuery,
  useShortLinkListQuery,
  useShortLinkSettingsInfoQuery,
} from 'core/graphql';
import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import { SHORT_LINK_ROLES } from '../Esign/useEsignPermissions';
import { useStableRefetch } from '../Groups/useStableRefetch';
import {
  IShortLinkFilters,
  SHORT_LINK_CHART_DAYS,
  SHORT_LINK_CLICKS_LIMIT,
  SHORT_LINK_PAGE_SIZE,
  ShortLinkSort,
  toShortLinkQueryVariables,
} from './shortLinkModel';

export type IShortLinkRow = ShortLinkListQuery['shortLinks'][number];

export type IShortLinkClick = ShortLinkClickListQuery['shortLinkClicks'][number];

export type IShortLinkSummary = ShortLinkListQuery['shortLinkSummary'];

export type IShortLinkAuthor = ShortLinkFacetListQuery['shortLinkFacets']['authors'][number];

export type IShortLinkDay = ShortLinkDailyClickListQuery['shortLinkDailyClicks'][number];

const EMPTY_ROWS: IShortLinkRow[] = [];
const EMPTY_CLICKS: IShortLinkClick[] = [];
const EMPTY_STRINGS: string[] = [];
const EMPTY_AUTHORS: IShortLinkAuthor[] = [];
const EMPTY_DAYS: IShortLinkDay[] = [];
const EMPTY_SUMMARY: IShortLinkSummary = {
  total: 0,
  live: 0,
  humanClicks: 0,
  clicks: 0,
};

/**
 * Открыт ли раздел текущей роли (`SHORT_LINK_ROLES`). Не защита — её держит
 * бэкенд, — а способ НЕ слать запрос без прав: отказ отклоняет промис Apollo
 * и рисует ошибку поверх экрана.
 */
export const useShortLinkAccess = (): boolean => {
  const { me } = useReactiveVar(userStateVar);
  const roles = (me?.roles ?? []) as UserRole[];
  return roles.some((role) => SHORT_LINK_ROLES.includes(role));
};

export interface IShortLinkSettings {
  /** `SHORTLINK_BASE_URL` задан: можно создавать ручные ссылки. */
  enabled: boolean;
  /** Начало короткой ссылки — для подсказки в окне создания. */
  baseUrl: string | null;
  loading: boolean;
}

/**
 * Настроен ли сократитель. Пока ответа нет — считаем выключенным: мигнуть
 * кнопкой «Создать» хуже, чем показать её на полсекунды позже.
 */
export const useShortLinkSettings = (): IShortLinkSettings => {
  const canOpen = useShortLinkAccess();
  const { data, loading } = useShortLinkSettingsInfoQuery({
    skip: !canOpen,
    fetchPolicy: 'cache-first',
  });
  return {
    enabled: Boolean(data?.shortLinkSettings.enabled),
    baseUrl: data?.shortLinkSettings.baseUrl ?? null,
    loading,
  };
};

export interface IUseShortLinkList {
  rows: IShortLinkRow[];
  total: number;
  /** Сводка по тем же фильтрам: ссылок, действуют, переходов людей и всего. */
  summary: IShortLinkSummary;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Список ссылок с фильтрами, порядком и страницей (`limit`/`offset`), и
 * сводка по тем же фильтрам — одним запросом.
 *
 * Страница — это ПЕРЕМЕННЫЕ запроса, а не `fetchMore`-склейка: перезапрос
 * после мутации перечитывает ровно текущую страницу и не схлопывает
 * накопленное (грабли `apollo-refetch-collapses-pages`).
 *
 * При смене фильтра новых данных ещё нет — таблица держит прошлую выдачу
 * (`previousData`), а не превращается в спиннер и не мигает «ничего не
 * нашлось».
 */
export const useShortLinkList = (
  filters: IShortLinkFilters,
  sort: ShortLinkSort,
  offset: number,
): IUseShortLinkList => {
  const canOpen = useShortLinkAccess();
  const { data, previousData, loading, error, refetch } = useShortLinkListQuery(
    {
      variables: {
        ...toShortLinkQueryVariables(filters),
        orderBy: sort,
        limit: SHORT_LINK_PAGE_SIZE,
        offset,
      },
      skip: !canOpen,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false,
    },
  );
  const shown = data ?? previousData;
  return {
    rows: shown?.shortLinks ?? EMPTY_ROWS,
    total: shown?.shortLinkSummary.total ?? 0,
    summary: shown?.shortLinkSummary ?? EMPTY_SUMMARY,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

export interface IUseShortLinkFacets {
  /** Все типы, что встречаются в ссылках юрлица (коды и свои). */
  channels: string[];
  tags: string[];
  authors: IShortLinkAuthor[];
  refetch: () => void;
}

/**
 * Справочник для фильтров и подсказок формы: типы, метки и авторы ссылок
 * юрлица. Целиком, а не из отфильтрованного списка — иначе выпадашка после
 * выбора схлопывалась бы до выбранного значения.
 */
export const useShortLinkFacets = (): IUseShortLinkFacets => {
  const canOpen = useShortLinkAccess();
  const { data, refetch } = useShortLinkFacetListQuery({
    skip: !canOpen,
    fetchPolicy: 'cache-and-network',
  });
  return {
    channels: data?.shortLinkFacets.channels ?? EMPTY_STRINGS,
    tags: data?.shortLinkFacets.tags ?? EMPTY_STRINGS,
    authors: data?.shortLinkFacets.authors ?? EMPTY_AUTHORS,
    refetch: useStableRefetch(refetch),
  };
};

export interface IUseShortLink {
  link: IShortLinkRow | null;
  loading: boolean;
  error?: ApolloError;
}

/**
 * Карточка одной ссылки. `id = null` — карточка закрыта, запроса нет.
 * Пока ответ идёт, страница подкладывает строку из списка — карточка
 * открывается сразу, а не пустым окном.
 */
export const useShortLink = (id: string | null): IUseShortLink => {
  const canOpen = useShortLinkAccess();
  const { data, loading, error } = useShortLinkCardQuery({
    variables: { id: id || '' },
    skip: !id || !canOpen,
    fetchPolicy: 'cache-and-network',
  });
  return { link: data?.shortLink ?? null, loading, error };
};

export interface IUseShortLinkClicks {
  clicks: IShortLinkClick[];
  loading: boolean;
  error?: ApolloError;
}

/** Последние переходы по ссылке (для карточки). */
export const useShortLinkClicks = (id: string | null): IUseShortLinkClicks => {
  const canOpen = useShortLinkAccess();
  const { data, loading, error } = useShortLinkClickListQuery({
    variables: { id: id || '', limit: SHORT_LINK_CLICKS_LIMIT },
    skip: !id || !canOpen,
    fetchPolicy: 'cache-and-network',
  });
  return { clicks: data?.shortLinkClicks ?? EMPTY_CLICKS, loading, error };
};

export interface IUseShortLinkDailyClicks {
  days: IShortLinkDay[];
  loading: boolean;
  error?: ApolloError;
}

/**
 * Переходы по дням за `days` суток (по Варшаве, с нулями) — график карточки.
 */
export const useShortLinkDailyClicks = (
  id: string | null,
  days = SHORT_LINK_CHART_DAYS,
): IUseShortLinkDailyClicks => {
  const canOpen = useShortLinkAccess();
  const { data, loading, error } = useShortLinkDailyClickListQuery({
    variables: { id: id || '', days },
    skip: !id || !canOpen,
    fetchPolicy: 'cache-and-network',
  });
  return {
    days: data?.shortLinkDailyClicks ?? EMPTY_DAYS,
    loading,
    error,
  };
};
